import { Injectable } from '@angular/core';
import { HeaderKeyMap } from '@core/enums/http-requests.enum';
import { BaseApiService } from '@core/services/base-api.service';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import type {
  ContractDetailsQueryParameters,
  ContractDetailsReveal,
  ResponseContractDetailsDto,
} from '../types/contracts.types';

const ContractsEndpoints = {
  organizationDetails: 'contract/organization/details',
  organizationSync: 'contract/organization/sync',
} as const;

// The reveal flags are only ever sent to turn a reveal on; omitting them means "off", which is the
// endpoint's default.
const RevealFlagValue = 'true';

@Injectable()
export class ContractsService extends BaseApiService {
  private readonly featureRole = NamedRoleMap.InternalUser;

  /**
   * Fetches the service contracts (enriched with line items and matched work item types) for an organization.
   * The backend returns only contracts active today (`state = ACTIVE` and `startDate <= now <= endDate`) and
   * only live line items unless the corresponding reveal flag is passed. Flags are sent only when true, so
   * the default request stays a plain lookup.
   *
   * @param {UUID} organizationId - The organization to load contracts for.
   * @param {ContractDetailsReveal} [reveal] - Which normally-hidden records to include.
   * @returns {Promise<ResponseContractDetailsDto[]>} A promise resolving to the contract details.
   */
  public getContractDetails(
    organizationId: UUID,
    reveal: ContractDetailsReveal = {},
  ): Promise<ResponseContractDetailsDto[]> {
    const parameters: ContractDetailsQueryParameters = {
      ...(reveal.shouldIncludeInactive && { includeInactive: RevealFlagValue }),
      ...(reveal.shouldIncludeDeletedLineItems && { includeDeletedLineItems: RevealFlagValue }),
    };

    return this.get<ResponseContractDetailsDto[], ContractDetailsQueryParameters>(
      ContractsEndpoints.organizationDetails,
      this.getRequestOptions<ContractDetailsQueryParameters>(this.featureRole, {
        customHeaders: {
          [HeaderKeyMap.OrganizationId]: organizationId,
        },
        params: parameters,
      }),
    );
  }

  /**
   * Triggers a (re-)synchronization of the organization's service contracts from Salesforce via the CAS wrapper
   * endpoint. This is a long-running call; callers must guard against concurrent invocations.
   *
   * @param {UUID} organizationId - The organization whose contracts should be re-synced from Salesforce.
   * @returns {Promise<{ organizationId: UUID }>} A promise resolving once the sync completes.
   */
  public syncFromSalesforce(organizationId: UUID): Promise<{ organizationId: UUID }> {
    return this.post<{ organizationId: UUID }, object>(
      ContractsEndpoints.organizationSync,
      {},
      this.getRequestOptions(this.featureRole, {
        customHeaders: {
          [HeaderKeyMap.OrganizationId]: organizationId,
        },
      }),
    );
  }
}
